import React, { useState, useEffect } from 'react';
import { ProductFormProps, Product, ProductImage, ProductSize, ProductFlavor } from '../../../types';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';
import ImageUploader from './ImageUploader';
import './ProductForm.css';
import { v4 as uuidv4 } from 'uuid';

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  collections,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [images, setImages] = useState<ProductImage[]>([]);
  const [flavors, setFlavors] = useState<ProductFlavor[]>([]);
  const [sizes, setSizes] = useState<ProductSize[]>([]);
  const [availableShapes, setAvailableShapes] = useState<Array<'Round' | 'Heart' | 'Square' | 'Star'>>([]);
  const [isActive, setIsActive] = useState(true);
  const [availableLayers, setAvailableLayers] = useState<string[]>([]);
  const [maxFlavorsSelectable, setMaxFlavorsSelectable] = useState<number>(1);
  const [fillings, setFillings] = useState<string[]>([]);
  const [coverIcingOptions, setCoverIcingOptions] = useState<string[]>([]);
  const [addOns, setAddOns] = useState<string[]>([]);
  const [matchingCupcakes, setMatchingCupcakes] = useState<string[]>([]);
  const [enableBaseColor, setEnableBaseColor] = useState<boolean>(true);
  const [enablePipingColor, setEnablePipingColor] = useState<boolean>(true);
  const [enableMessageOnCake, setEnableMessageOnCake] = useState<boolean>(true);
  const [enableImageOnCake, setEnableImageOnCake] = useState<boolean>(true);
  const [enableSampleImage, setEnableSampleImage] = useState<boolean>(true);
  
  // New flavor and size state for adding
  const [newFlavor, setNewFlavor] = useState('');
  const [newFlavorPrice, setNewFlavorPrice] = useState<number | ''>('');
  const [newSize, setNewSize] = useState({
    name: '',
    servingSize: 0,
    price: 0
  });
  
  // New filling and cover icing state for adding
  const [newFilling, setNewFilling] = useState('');
  const [newFillingPrice, setNewFillingPrice] = useState<number | ''>('');
  const [newCoverIcing, setNewCoverIcing] = useState('');
  const [newCoverIcingPrice, setNewCoverIcingPrice] = useState<number | ''>('');
  
  // New add-ons and matching cupcakes state for adding
  const [newAddOn, setNewAddOn] = useState('');
  const [newAddOnPrice, setNewAddOnPrice] = useState<number | ''>('');
  const [newMatchingCupcake, setNewMatchingCupcake] = useState('');
  const [newMatchingCupcakePrice, setNewMatchingCupcakePrice] = useState<number | ''>('');
  // Quantity tier editor state (Desserts only)
  const [tierSizeIndex, setTierSizeIndex] = useState<number>(0);
  const [newTierQuantity, setNewTierQuantity] = useState<string>('');
  const [newTierPrice, setNewTierPrice] = useState<string>('');

  // Initialize form with product data if editing
  useEffect(() => {
    if (product) {
      setName(product.name);
      setDescription(product.description);
      setCollectionId(product.collectionId);
      setImages(product.images);
      setFlavors(product.flavors);
      setSizes(product.sizes);
      setAvailableShapes(product.availableShapes);
      setIsActive(product.isActive);
      setAvailableLayers(product.availableLayers || []);
      setMaxFlavorsSelectable(product.maxFlavorsSelectable || 1);
      setFillings(product.fillings || []);
      setCoverIcingOptions(product.coverIcingOptions || []);
      setAddOns(product.addOns || []);
      setMatchingCupcakes(product.matchingCupcakes || []);
      setEnableBaseColor(product.enableBaseColor !== undefined ? product.enableBaseColor : true);
      setEnablePipingColor(product.enablePipingColor !== undefined ? product.enablePipingColor : true);
      setEnableMessageOnCake(product.enableMessageOnCake !== undefined ? product.enableMessageOnCake : true);
      setEnableImageOnCake(product.enableImageOnCake !== undefined ? product.enableImageOnCake : true);
      setEnableSampleImage(product.enableSampleImage !== undefined ? product.enableSampleImage : true);
      setTierSizeIndex(0);
    } else {
      // Default values for new product
      setName('');
      setDescription('');
      setCollectionId(collections.length > 0 ? collections[0].id : '');
      setImages([]);
      setFlavors([]);
      setSizes([]);
      setAvailableShapes(['Round']);
      setIsActive(true);
      setAvailableLayers([]);
      setMaxFlavorsSelectable(1);
      setFillings([]);
      setCoverIcingOptions([]);
      setAddOns([]);
      setMatchingCupcakes([]);
      // Default behavior: enable colors for cake collections
      setEnableBaseColor(true);
      setEnablePipingColor(true);
      setEnableMessageOnCake(true);
      setEnableImageOnCake(true);
      setEnableSampleImage(true);
      setTierSizeIndex(0);
      setNewTierQuantity('');
      setNewTierPrice('');
    }
  }, [product, collections]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedCollection = collections.find(c => c.id === collectionId);
    
    if (!selectedCollection) return;
    
    // Initialize product data differently based on whether we're editing or creating
    let productData: Partial<Product>;
    
    if (product) {
      // For updates, only include fields that have been modified
      productData = { id: product.id };
      
      // Only include fields that have values and have changed
      if (name !== product.name && name.trim() !== '') productData.name = name;
      if (description !== product.description && description !== undefined) productData.description = description;
      if (collectionId !== product.collectionId && collectionId) {
        productData.collectionId = collectionId;
        productData.collection = selectedCollection.name;
      }
      if (JSON.stringify(images) !== JSON.stringify(product.images)) productData.images = images || [];
      if (JSON.stringify(flavors) !== JSON.stringify(product.flavors)) productData.flavors = flavors || [];
      if (JSON.stringify(sizes) !== JSON.stringify(product.sizes)) productData.sizes = sizes || [];
      if (JSON.stringify(availableShapes) !== JSON.stringify(product.availableShapes)) productData.availableShapes = availableShapes || [];
      if (isActive !== product.isActive) productData.isActive = isActive;
      if (JSON.stringify(availableLayers) !== JSON.stringify(product.availableLayers)) productData.availableLayers = availableLayers || [];
      if (maxFlavorsSelectable !== product.maxFlavorsSelectable) productData.maxFlavorsSelectable = maxFlavorsSelectable;
      if (JSON.stringify(fillings) !== JSON.stringify(product.fillings)) productData.fillings = fillings || [];
      if (JSON.stringify(coverIcingOptions) !== JSON.stringify(product.coverIcingOptions)) productData.coverIcingOptions = coverIcingOptions || [];
      if (JSON.stringify(addOns) !== JSON.stringify(product.addOns)) productData.addOns = addOns || [];
      if (JSON.stringify(matchingCupcakes) !== JSON.stringify(product.matchingCupcakes)) productData.matchingCupcakes = matchingCupcakes || [];
      if (enableBaseColor !== product.enableBaseColor) productData.enableBaseColor = enableBaseColor;
      if (enablePipingColor !== product.enablePipingColor) productData.enablePipingColor = enablePipingColor;
      if (enableMessageOnCake !== product.enableMessageOnCake) productData.enableMessageOnCake = enableMessageOnCake;
      if (enableImageOnCake !== product.enableImageOnCake) productData.enableImageOnCake = enableImageOnCake;
      if (enableSampleImage !== product.enableSampleImage) productData.enableSampleImage = enableSampleImage;
    } else {
      // For new products, include all fields
      productData = {
        name,
        description,
        collection: selectedCollection.name,
        collectionId,
        images,
        flavors,
        sizes,
        availableShapes,
        isActive,
        availableLayers,
        maxFlavorsSelectable,
        fillings,
        coverIcingOptions,
        addOns,
        matchingCupcakes,
        enableBaseColor,
        enablePipingColor,
        enableMessageOnCake,
        enableImageOnCake,
        enableSampleImage
      };
    }
    
    onSubmit(productData);
  };

  const handleAddFlavor = () => {
    if (!newFlavor.trim()) return;
    
    const flavorPrice = typeof newFlavorPrice === 'number' && newFlavorPrice >= 0 
      ? newFlavorPrice 
      : undefined;
    
    setFlavors([
      ...flavors,
      { 
        name: newFlavor, 
        available: true,
        price: flavorPrice
      }
    ]);
    
    setNewFlavor('');
    setNewFlavorPrice('');
  };

  const handleRemoveFlavor = (index: number) => {
    const updatedFlavors = [...flavors];
    updatedFlavors.splice(index, 1);
    setFlavors(updatedFlavors);
  };

  const handleToggleFlavor = (index: number) => {
    const updatedFlavors = [...flavors];
    updatedFlavors[index] = {
      ...updatedFlavors[index],
      available: !updatedFlavors[index].available
    };
    setFlavors(updatedFlavors);
  };
  
  const handleFlavorPriceChange = (index: number, price: string) => {
    const priceValue = price === '' ? undefined : parseFloat(price);
    
    const updatedFlavors = [...flavors];
    updatedFlavors[index] = {
      ...updatedFlavors[index],
      price: priceValue !== undefined && !isNaN(priceValue) && priceValue >= 0 ? priceValue : undefined
    };
    setFlavors(updatedFlavors);
  };

  const handleAddSize = () => {
    // Allow "serves" to be optional for Desserts collection
    const selectedCollection = collections.find(c => c.id === collectionId);
    const isDessertsCollection = selectedCollection?.name === 'Desserts';

    if (!newSize.name || newSize.price <= 0) return;
    if (!isDessertsCollection && newSize.servingSize <= 0) return;
    
    setSizes([
      ...sizes,
      { ...newSize }
    ]);
    
    setNewSize({
      name: '',
      servingSize: 0,
      price: 0
    });
  };

  const handleRemoveSize = (index: number) => {
    const updatedSizes = [...sizes];
    updatedSizes.splice(index, 1);
    setSizes(updatedSizes);
  };
  
  const handleEditSize = (index: number, field: keyof ProductSize, value: string | number) => {
    const updatedSizes = [...sizes];
    if (field === 'name') {
      updatedSizes[index] = { ...updatedSizes[index], name: value as string };
    } else if (field === 'servingSize') {
      updatedSizes[index] = { ...updatedSizes[index], servingSize: parseInt(value as string) || 0 };
    } else if (field === 'price') {
      updatedSizes[index] = { ...updatedSizes[index], price: parseFloat(value as string) || 0 };
    }
    setSizes(updatedSizes);
  };

  const handleShapeToggle = (shape: 'Round' | 'Heart' | 'Square' | 'Star') => {
    if (availableShapes.includes(shape)) {
      setAvailableShapes(availableShapes.filter(s => s !== shape));
    } else {
      setAvailableShapes([...availableShapes, shape]);
    }
  };

  // Fillings handlers
  const handleAddFilling = () => {
    if (!newFilling.trim()) return;
    
    const fillingPrice = typeof newFillingPrice === 'number' && newFillingPrice > 0 
      ? newFillingPrice 
      : undefined;
    
    const fillingString = fillingPrice 
      ? `${newFilling} +€${fillingPrice}`
      : newFilling;
    
    setFillings([...fillings, fillingString]);
    setNewFilling('');
    setNewFillingPrice('');
  };

  const handleRemoveFilling = (index: number) => {
    const updatedFillings = [...fillings];
    updatedFillings.splice(index, 1);
    setFillings(updatedFillings);
  };

  // Cover icing handlers
  const handleAddCoverIcing = () => {
    if (!newCoverIcing.trim()) return;
    
    const icingPrice = typeof newCoverIcingPrice === 'number' && newCoverIcingPrice > 0 
      ? newCoverIcingPrice 
      : undefined;
    
    const icingString = icingPrice 
      ? `${newCoverIcing} +€${icingPrice}`
      : newCoverIcing;
    
    setCoverIcingOptions([...coverIcingOptions, icingString]);
    setNewCoverIcing('');
    setNewCoverIcingPrice('');
  };

  const handleRemoveCoverIcing = (index: number) => {
    const updatedCoverIcing = [...coverIcingOptions];
    updatedCoverIcing.splice(index, 1);
    setCoverIcingOptions(updatedCoverIcing);
  };

  // Add-ons handlers
  const handleAddAddOn = () => {
    if (!newAddOn.trim()) return;
    
    const addOnPrice = typeof newAddOnPrice === 'number' && newAddOnPrice > 0 
      ? newAddOnPrice 
      : undefined;
    
    const addOnString = addOnPrice 
      ? `${newAddOn} +€${addOnPrice}`
      : newAddOn;
    
    setAddOns([...addOns, addOnString]);
    setNewAddOn('');
    setNewAddOnPrice('');
  };

  const handleRemoveAddOn = (index: number) => {
    const updatedAddOns = [...addOns];
    updatedAddOns.splice(index, 1);
    setAddOns(updatedAddOns);
  };

  // Matching cupcakes handlers
  const handleAddMatchingCupcake = () => {
    if (!newMatchingCupcake.trim()) return;
    
    const cupcakePrice = typeof newMatchingCupcakePrice === 'number' && newMatchingCupcakePrice > 0 
      ? newMatchingCupcakePrice 
      : undefined;
    
    const cupcakeString = cupcakePrice 
      ? `${newMatchingCupcake} +€${cupcakePrice}`
      : newMatchingCupcake;
    
    setMatchingCupcakes([...matchingCupcakes, cupcakeString]);
    setNewMatchingCupcake('');
    setNewMatchingCupcakePrice('');
  };

  const handleRemoveMatchingCupcake = (index: number) => {
    const updatedMatchingCupcakes = [...matchingCupcakes];
    updatedMatchingCupcakes.splice(index, 1);
    setMatchingCupcakes(updatedMatchingCupcakes);
  };

  // Quantity tiers handlers (Desserts only)
  const handleAddQuantityTier = () => {
    if (!sizes.length) return;
    const sizeIdx = Math.min(Math.max(tierSizeIndex, 0), sizes.length - 1);
    const quantity = parseInt(newTierQuantity, 10);
    const price = parseFloat(newTierPrice);
    if (!quantity || quantity <= 0 || isNaN(price) || price <= 0) return;

    const updatedSizes = [...sizes];
    const size = { ...updatedSizes[sizeIdx] };
    const existingTiers = size.quantityTiers || [];
    size.quantityTiers = [...existingTiers, { quantity, price }];
    updatedSizes[sizeIdx] = size;
    setSizes(updatedSizes);
    setNewTierQuantity('');
    setNewTierPrice('');
  };

  const handleRemoveQuantityTier = (sizeIdx: number, tierIdx: number) => {
    const updatedSizes = [...sizes];
    const size = { ...updatedSizes[sizeIdx] };
    if (!size.quantityTiers) return;
    const tiers = [...size.quantityTiers];
    tiers.splice(tierIdx, 1);
    size.quantityTiers = tiers;
    updatedSizes[sizeIdx] = size;
    setSizes(updatedSizes);
  };

  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
      </div>
      
      <div className="form-section">
        <h3>Basic Information</h3>
        
        <div className="basic-info-container">
          <div className="basic-info-column">
            <div className="form-group">
              <label htmlFor="product-name">Product Name</label>
              <input
                id="product-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={!product} // Only required for new products
                placeholder="Enter product name"
                className="form-control"
              />
            </div>
          </div>
          
          <div className="basic-info-column">
            <div className="form-group">
              <label htmlFor="product-collection">Collection</label>
              <select
                id="product-collection"
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                required={!product} // Only required for new products
                className="form-control"
              >
                {collections.map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="basic-info-column">
            <div className="form-group">
              <label htmlFor="product-status">Status</label>
              <div className="toggle-switch">
                <input
                  id="product-status"
                  type="checkbox"
                  checked={isActive}
                  onChange={() => setIsActive(!isActive)}
                />
                <label htmlFor="product-status">
                  {isActive ? 'Active' : 'Inactive'}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="basic-info-container">
          <div className="basic-info-column">
            <div className="form-group">
              <label>Customization Options</label>
              <div className="color-options-toggles">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={enableBaseColor}
                    onChange={() => setEnableBaseColor(!enableBaseColor)}
                  />
                  <span>Enable base color selection</span>
                </label>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={enablePipingColor}
                    onChange={() => setEnablePipingColor(!enablePipingColor)}
                  />
                  <span>Enable piping color selection</span>
                </label>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={enableMessageOnCake}
                    onChange={() => setEnableMessageOnCake(!enableMessageOnCake)}
                  />
                  <span>Enable message on cake</span>
                </label>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={enableImageOnCake}
                    onChange={() => setEnableImageOnCake(!enableImageOnCake)}
                  />
                  <span>Enable image on cake</span>
                </label>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={enableSampleImage}
                    onChange={() => setEnableSampleImage(!enableSampleImage)}
                  />
                  <span>Enable sample cake image</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="product-description">Description</label>
          <textarea
            id="product-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter product description"
            className="form-control"
            rows={3}
          />
        </div>
      </div>
      
      <div className="form-section">
        <ImageUploader
          images={images}
          onImagesChange={setImages}
        />
      </div>
      
      <div className="form-section">
        <h3>Available Shapes</h3>
        <p className="form-helper-text">Click on a shape to add or remove it from available options</p>
        <div className="shapes-container">
          {(['Round', 'Heart', 'Square', 'Star'] as const).map(shape => (
            <div 
              key={shape} 
              className={`shape-option ${availableShapes.includes(shape) ? 'selected' : ''}`}
              onClick={() => handleShapeToggle(shape)}
            >
              {shape}
              <span className="shape-icon">
                {availableShapes.includes(shape) ? '+' : '-'}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="form-section">
        <h3>Flavors & Sizes Configuration</h3>
        
        <div className="flavors-sizes-container">
          <div className="flavors-column">
            <h4>Flavors</h4>
            
            <div className="flavors-list">
              {flavors.map((flavor, index) => (
                <div key={index} className="flavor-item">
                  <div className="flavor-info">
                    <div className="flavor-name">
                      <div 
                        className={`flavor-status ${flavor.available ? 'available' : 'unavailable'}`}
                        onClick={() => handleToggleFlavor(index)}
                      >
                        {flavor.available ? 'Available' : 'Unavailable'}
                      </div>
                      {flavor.name}
                    </div>
                    <div className="flavor-price">
                      <label>Additional Price (€):</label>
                      <input
                        type="number"
                        value={flavor.price !== undefined ? flavor.price : ''}
                        onChange={(e) => handleFlavorPriceChange(index, e.target.value)}
                        placeholder="0.00"
                        className="flavor-price-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveFlavor(index)}
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="add-flavor">
              <div className="flavor-inputs">
                <input
                  type="text"
                  value={newFlavor}
                  onChange={(e) => setNewFlavor(e.target.value)}
                  placeholder="Add a new flavor"
                  className="form-control"
                />
                <input
                  type="number"
                  value={newFlavorPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setNewFlavorPrice('');
                    } else {
                      const numValue = parseFloat(value);
                      setNewFlavorPrice(isNaN(numValue) ? '' : numValue);
                    }
                  }}
                  placeholder="Price (€)"
                  className="form-control flavor-price-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleAddFlavor}
              >
                <Icon type="add" size="small" /> Add
              </Button>
            </div>
          </div>
          
          <div className="sizes-column">
            <h4>Layers & Pricing</h4>
            
            <div className="sizes-list">
              {sizes.map((size, index) => (
                <div key={index} className="size-item">
                  <div className="size-info">
                    <div className="size-name">
                      <input 
                        type="text" 
                        value={size.name}
                        onChange={(e) => handleEditSize(index, 'name', e.target.value)}
                        className="size-edit-input"
                      />
                    </div>
                    <div className="size-details">
                      <div className="size-detail-item">
                        <label>Serves:</label>
                        <input 
                          type="number" 
                          value={size.servingSize}
                          onChange={(e) => handleEditSize(index, 'servingSize', e.target.value)}
                          className="size-edit-input size-edit-number"
                        min="0"
                        />
                      </div>
                      <div className="size-detail-item">
                        <label>Price: €</label>
                        <input 
                          type="number" 
                          value={size.price}
                          onChange={(e) => handleEditSize(index, 'price', e.target.value)}
                          className="size-edit-input size-edit-number"
                        min="0.01"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveSize(index)}
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="add-size">
              <div className="size-inputs">
                <input
                  type="text"
                  value={newSize.name}
                  onChange={(e) => setNewSize({...newSize, name: e.target.value})}
                  placeholder="Layers"
                  className="form-control"
                />
                <input
                  type="number"
                  value={newSize.servingSize || ''}
                  onChange={(e) => setNewSize({...newSize, servingSize: parseInt(e.target.value) || 0})}
                  placeholder="Serving size (optional for Desserts)"
                  className="form-control"
                  min="0"
                />
                <input
                  type="number"
                  value={newSize.price || ''}
                  onChange={(e) => setNewSize({...newSize, price: parseFloat(e.target.value) || 0})}
                  placeholder="Price (€)"
                  className="form-control"
                  min="0.01"
                  step="0.01"
                />
              </div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleAddSize}
              >
                <Icon type="add" size="small" /> Add
              </Button>
            </div>

            {/* Dessert quantity & pricing configuration (per size) */}
            {sizes.length > 0 && collections.length > 0 && (() => {
              const selectedCollection = collections.find(c => c.id === collectionId);
              const isDessertsCollection = selectedCollection?.name === 'Desserts';
              return isDessertsCollection ? (
                <div className="dessert-quantity-config">
                  <h4>Dessert Quantity & Pricing</h4>
                  <p className="form-helper-text">
                    Configure available quantities and bundle prices for Desserts. These options are used on the customer product page instead of the free quantity input.
                  </p>
                  <div className="dessert-quantity-controls">
                    <div className="form-group">
                      <label htmlFor="tier-size-select">Size</label>
                      <select
                        id="tier-size-select"
                        className="form-control"
                        value={Math.min(tierSizeIndex, sizes.length - 1)}
                        onChange={(e) => setTierSizeIndex(parseInt(e.target.value, 10) || 0)}
                      >
                        {sizes.map((size, index) => (
                          <option key={index} value={index}>
                            {size.name || `Size ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Quantity tiers for selected size</label>
                      <div className="quantity-tiers-list">
                        {(sizes[tierSizeIndex]?.quantityTiers || []).map((tier, index) => (
                          <div key={`${tier.quantity}-${index}`} className="quantity-tier-pill">
                            <span>{tier.quantity} pcs</span>
                            <span>€{tier.price.toFixed(2)}</span>
                            <button
                              type="button"
                              className="remove-button"
                              onClick={() => handleRemoveQuantityTier(tierSizeIndex, index)}
                            >
                              <Icon type="delete" size="small" />
                            </button>
                          </div>
                        ))}
                        {(sizes[tierSizeIndex]?.quantityTiers || []).length === 0 && (
                          <p className="form-help-text">
                            No quantity tiers added yet. Add at least one to enable tiered quantities on the storefront.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="add-quantity-tier">
                      <div className="quantity-tier-inputs">
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          placeholder="Quantity (e.g. 6)"
                          value={newTierQuantity}
                          onChange={(e) => setNewTierQuantity(e.target.value)}
                        />
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          className="form-control"
                          placeholder="Bundle price (€)"
                          value={newTierPrice}
                          onChange={(e) => setNewTierPrice(e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleAddQuantityTier}
                      >
                        <Icon type="add" size="small" /> Add tier
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      </div>
      
      {/* Hide layers & flavors config for Desserts collection */}
      {collections.find(c => c.id === collectionId)?.name !== 'Desserts' && (
      <div className="form-section">
        <h3>Layers & Flavors Configuration</h3>
        
        <div className="layers-flavors-container">
          <div className="layers-column">
            <h4>Available Layers</h4>
            <div className="form-group">
              <label htmlFor="available-layers">Available Layers (comma-separated)</label>
              <input
                id="available-layers"
                type="text"
                value={availableLayers.join(', ')}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow typing spaces and commas, only filter empty values when user finishes typing
                  const layers = value.split(',').map(layer => layer.trim());
                  setAvailableLayers(layers);
                }}
                onBlur={(e) => {
                  // Clean up empty values when user finishes editing
                  const layers = e.target.value.split(',').map(layer => layer.trim()).filter(layer => layer);
                  setAvailableLayers(layers);
                }}
                placeholder="e.g. 2, 3, 4 or 2 Layers, 3 Layers, 4 Layers"
                className="form-control"
              />
              </div>
          </div>
          
          <div className="flavors-column">
            <h4>Maximum Flavors</h4>
            <div className="form-group">
              <label htmlFor="max-flavors">Maximum Flavors Selectable</label>
              <input
                id="max-flavors"
                type="number"
                value={maxFlavorsSelectable}
                onChange={(e) => setMaxFlavorsSelectable(parseInt(e.target.value) || 1)}
                min="1"
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>
      )}
      
      {/* Hide fillings & cover icing for Desserts collection */}
      {collections.find(c => c.id === collectionId)?.name !== 'Desserts' && (
      <div className="form-section">
        <h3>Fillings & Cover Icing Options</h3>
        
        <div className="fillings-icing-container">
          <div className="fillings-column">
            <h4>Fillings</h4>
            
            <div className="fillings-list">
              {fillings.map((filling, index) => (
                <div key={index} className="filling-item">
                  <div className="filling-info">
                    <span className="filling-name">{filling}</span>
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveFilling(index)}
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="add-filling">
              <div className="filling-inputs">
                <input
                  type="text"
                  value={newFilling}
                  onChange={(e) => setNewFilling(e.target.value)}
                  placeholder="Add a new filling"
                  className="form-control"
                />
                <input
                  type="number"
                  value={newFillingPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setNewFillingPrice('');
                    } else {
                      const numValue = parseFloat(value);
                      setNewFillingPrice(isNaN(numValue) ? '' : numValue);
                    }
                  }}
                  placeholder="Additional Price (€)"
                  className="form-control filling-price-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleAddFilling}
              >
                <Icon type="add" size="small" /> Add
              </Button>
            </div>
          </div>
          
          <div className="cover-icing-column">
            <h4>Cover Icing Options</h4>
            
            <div className="cover-icing-list">
              {coverIcingOptions.map((icing, index) => (
                <div key={index} className="cover-icing-item">
                  <div className="cover-icing-info">
                    <span className="cover-icing-name">{icing}</span>
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveCoverIcing(index)}
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="add-cover-icing">
              <div className="cover-icing-inputs">
                <input
                  type="text"
                  value={newCoverIcing}
                  onChange={(e) => setNewCoverIcing(e.target.value)}
                  placeholder="Add a new cover icing option"
                  className="form-control"
                />
                <input
                  type="number"
                  value={newCoverIcingPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setNewCoverIcingPrice('');
                    } else {
                      const numValue = parseFloat(value);
                      setNewCoverIcingPrice(isNaN(numValue) ? '' : numValue);
                    }
                  }}
                  placeholder="Additional Price (€)"
                  className="form-control cover-icing-price-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleAddCoverIcing}
              >
                <Icon type="add" size="small" /> Add
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}
      
      {/* Hide add-ons & matching cupcakes for Desserts collection */}
      {collections.find(c => c.id === collectionId)?.name !== 'Desserts' && (
      <div className="form-section">
        <h3>Add-ons & Matching Cupcakes</h3>
        
        <div className="addons-cupcakes-container">
          <div className="addons-column">
            <h4>Add-ons</h4>
            
            <div className="addons-list">
              {addOns.map((addOn, index) => (
                <div key={index} className="addon-item">
                  <div className="addon-info">
                    <span className="addon-name">{addOn}</span>
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveAddOn(index)}
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="add-addon">
              <div className="addon-inputs">
                <input
                  type="text"
                  value={newAddOn}
                  onChange={(e) => setNewAddOn(e.target.value)}
                  placeholder="Add a new add-on"
                  className="form-control"
                />
                <input
                  type="number"
                  value={newAddOnPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setNewAddOnPrice('');
                    } else {
                      const numValue = parseFloat(value);
                      setNewAddOnPrice(isNaN(numValue) ? '' : numValue);
                    }
                  }}
                  placeholder="Additional Price (€)"
                  className="form-control addon-price-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleAddAddOn}
              >
                <Icon type="add" size="small" /> Add
              </Button>
            </div>
          </div>
          
          <div className="cupcakes-column">
            <h4>Matching Cupcakes</h4>
            
            <div className="cupcakes-list">
              {matchingCupcakes.map((cupcake, index) => (
                <div key={index} className="cupcake-item">
                  <div className="cupcake-info">
                    <span className="cupcake-name">{cupcake}</span>
                  </div>
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => handleRemoveMatchingCupcake(index)}
                  >
                    <Icon type="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="add-cupcake">
              <div className="cupcake-inputs">
                <input
                  type="text"
                  value={newMatchingCupcake}
                  onChange={(e) => setNewMatchingCupcake(e.target.value)}
                  placeholder="Add a new matching cupcake"
                  className="form-control"
                />
                <input
                  type="number"
                  value={newMatchingCupcakePrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setNewMatchingCupcakePrice('');
                    } else {
                      const numValue = parseFloat(value);
                      setNewMatchingCupcakePrice(isNaN(numValue) ? '' : numValue);
                    }
                  }}
                  placeholder="Additional Price (€)"
                  className="form-control cupcake-price-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleAddMatchingCupcake}
              >
                <Icon type="add" size="small" /> Add
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}
      
      <div className="form-actions">
        <Button 
          type="button" 
          variant="secondary" 
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="primary" 
          disabled={loading}
        >
          {loading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
